import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, Check, X, Settings2, Filter, Plus, Trash2, ChevronsUpDown, Link2, Users } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { TemplateEditor } from "@/components/proposals";
import { getSimulator } from "@/lib/api/simulators";
import {
  getProposalTemplate,
  updateProposalTemplate,
  listProposalTemplates,
} from "@/lib/api/proposal-templates";
import { listAllOutputFieldGroups } from "@/lib/api/output-fields";
import { listAllFormFieldGroups } from "@/lib/api/form-fields";
import { getErrorMessage } from "@/lib/api/client";
import { AdminGuard } from "@/lib/auth/AdminGuard";
import type { TemplatePage, CanvasData, OutputField, OutputFieldGroupWithFields, FormField, FormFieldGroupWithFields, VisibilityConditions, ConditionOperator, ConditionLogic } from "@/types";

// Operator labels for the conditions editor
const operatorLabels: Record<ConditionOperator, string> = {
  equals: "est egal a",
  not_equals: "n'est pas egal a",
  greater_than: "est superieur a",
  greater_than_or_equals: "est superieur ou egal a",
  less_than: "est inferieur a",
  less_than_or_equals: "est inferieur ou egal a",
  contains: "contient",
  not_contains: "ne contient pas",
  is_empty: "est vide",
  is_not_empty: "n'est pas vide",
  in: "est parmi",
  not_in: "n'est pas parmi",
};

// Field interface for the search selector
interface FieldOption {
  slug: string;
  label: string;
  type: "input" | "output";
}

// Searchable field selector component with search bar
function FieldSearchSelector({
  value,
  onSelect,
  fields,
  placeholder = "Rechercher un champ...",
}: {
  value: string;
  onSelect: (slug: string) => void;
  fields: FieldOption[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  
  // Find the selected field to display
  const selectedField = fields.find((f) => f.slug === value);
  
  // Separate fields by type
  const inputFields = fields.filter((f) => f.type === "input");
  const outputFields = fields.filter((f) => f.type === "output");
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-56 justify-between font-normal"
        >
          {selectedField ? (
            <span className="truncate text-sm font-mono" title={selectedField.slug}>
              {selectedField.slug}
            </span>
          ) : (
            <span className="text-muted-foreground truncate">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder="Rechercher..." />
          <CommandList>
            <CommandEmpty>Aucun champ trouve.</CommandEmpty>
            {inputFields.length > 0 && (
              <CommandGroup heading="Champs d'entree (formulaire)">
                {inputFields.map((field) => (
                  <CommandItem
                    key={field.slug}
                    value={field.slug}
                    onSelect={() => {
                      onSelect(field.slug);
                      setOpen(false);
                    }}
                    className={cn(
                      "cursor-pointer",
                      value === field.slug && "bg-accent"
                    )}
                  >
                    <span className="text-sm font-mono">{field.slug}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {outputFields.length > 0 && (
              <CommandGroup heading="Champs de calcul (sorties)">
                {outputFields.map((field) => (
                  <CommandItem
                    key={field.slug}
                    value={field.slug}
                    onSelect={() => {
                      onSelect(field.slug);
                      setOpen(false);
                    }}
                    className={cn(
                      "cursor-pointer",
                      value === field.slug && "bg-accent"
                    )}
                  >
                    <span className="text-sm font-mono">{field.slug}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export const Route = createFileRoute(
  "/_dashboard/admin/simulators/$id/templates/$templateId"
)({
  component: EditTemplatePage,
  validateSearch: (search: Record<string, unknown>) => ({
    version: search.version ? Number(search.version) : undefined,
  }),
});

function EditTemplatePage() {
  const { id, templateId } = Route.useParams();
  const { version: urlVersionId } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const simulatorId = Number(id);
  const templateIdNum = Number(templateId);

  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [priority, setPriority] = useState(0);
  const [selectionConditions, setSelectionConditions] = useState<VisibilityConditions | null>(null);
  const [parentTemplateId, setParentTemplateId] = useState<number | null>(null);

  // Get simulator info
  const { data: simulator } = useQuery({
    queryKey: ["simulator", simulatorId],
    queryFn: () => getSimulator(simulatorId),
  });

  // Get template (with resolved pages)
  const {
    data: template,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["proposal-template", templateIdNum],
    queryFn: () => getProposalTemplate(templateIdNum),
  });

  // Use the versionId from URL, or from the template's version_id
  const activeVersionId = useMemo(() => {
    // Use URL version if available
    if (urlVersionId) return urlVersionId;
    // Use template's version_id if available
    if (template?.version_id) return template.version_id;
    // Fall back to computing from simulator data
    if (!simulator?.versions?.length) return null;
    const publishedVersion = simulator.versions.find(v => v.status === "published");
    if (publishedVersion) return publishedVersion.id;
    const sortedVersions = [...simulator.versions].sort((a, b) => b.version_number - a.version_number);
    return sortedVersions[0]?.id ?? null;
  }, [urlVersionId, template?.version_id, simulator?.versions]);

  // Fetch potential parent templates (templates without a parent, excluding this one)
  const { data: potentialParents } = useQuery({
    queryKey: ["proposal-templates", activeVersionId, "parents-only"],
    queryFn: () => listProposalTemplates(activeVersionId!, { active_only: false, parents_only: true }),
    enabled: !!activeVersionId,
  });

  // Filter out the current template from potential parents
  const availableParents = useMemo(() => {
    if (!potentialParents?.items) return [];
    return potentialParents.items.filter((t) => t.id !== templateIdNum);
  }, [potentialParents?.items, templateIdNum]);

  // Fetch output field groups for the simulator's version
  const { data: outputFieldGroups } = useQuery({
    queryKey: ["output-field-groups", activeVersionId],
    queryFn: () => listAllOutputFieldGroups(activeVersionId!),
    enabled: !!activeVersionId,
  });

  // Fetch form field groups for the simulator's version
  const { data: formFieldGroups } = useQuery({
    queryKey: ["form-field-groups", activeVersionId],
    queryFn: () => listAllFormFieldGroups(activeVersionId!),
    enabled: !!activeVersionId,
  });

  // Flatten output fields from groups
  const flattenOutputFields = (group: OutputFieldGroupWithFields): OutputField[] => {
    const fields = [...group.fields];
    for (const child of group.children) {
      fields.push(...flattenOutputFields(child));
    }
    return fields;
  };

  // Flatten form fields from groups
  const flattenFormFields = (group: FormFieldGroupWithFields): FormField[] => {
    const fields = [...group.fields];
    for (const child of group.children) {
      fields.push(...flattenFormFields(child));
    }
    return fields;
  };

  // Get all output fields for the template editor
  const allOutputFields = useMemo(() => {
    if (!outputFieldGroups) return [];
    const fields: OutputField[] = [];
    for (const group of outputFieldGroups) {
      fields.push(...flattenOutputFields(group));
    }
    return fields;
  }, [outputFieldGroups]);

  // Get all form fields for the template editor
  const allFormFields = useMemo(() => {
    if (!formFieldGroups) return [];
    const fields: FormField[] = [];
    for (const group of formFieldGroups) {
      fields.push(...flattenFormFields(group));
    }
    return fields;
  }, [formFieldGroups]);

  // Combined list of form and output fields for condition selector
  const allFieldsForConditions = useMemo(() => {
    const fields: FieldOption[] = [];
    for (const field of allFormFields) {
      fields.push({ slug: field.slug, label: `[Entree] ${field.label}`, type: "input" });
    }
    for (const field of allOutputFields) {
      fields.push({ slug: field.slug, label: `[Sortie] ${field.label}`, type: "output" });
    }
    return fields;
  }, [allFormFields, allOutputFields]);

  // Initialize settings from template data
  useEffect(() => {
    if (template) {
      setPriority(template.priority ?? 0);
      setSelectionConditions(template.selection_conditions ?? null);
      setParentTemplateId(template.parent_template_id ?? null);
    }
  }, [template]);

  // Update template mutation
  const updateMutation = useMutation({
    mutationFn: (data: { pages: TemplatePage[]; inheritedPageAdditions?: Record<string, CanvasData> | null }) =>
      updateProposalTemplate(templateIdNum, {
        pages_data: data.pages,
        inherited_page_additions: data.inheritedPageAdditions,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["proposal-template", templateIdNum],
      });
      queryClient.invalidateQueries({
        queryKey: ["proposal-templates", activeVersionId],
      });
      toast.success("Template enregistre");
    },
    onError: (error) => {
      toast.error("Erreur lors de l'enregistrement", {
        description: getErrorMessage(error),
      });
    },
  });

  // Update name mutation
  const updateNameMutation = useMutation({
    mutationFn: (name: string) =>
      updateProposalTemplate(templateIdNum, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["proposal-template", templateIdNum],
      });
      queryClient.invalidateQueries({
        queryKey: ["proposal-templates", activeVersionId],
      });
      setIsEditingName(false);
      toast.success("Nom mis a jour");
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });

  // Update settings mutation (priority, selection conditions, and parent)
  const updateSettingsMutation = useMutation({
    mutationFn: (data: { priority: number; selection_conditions: VisibilityConditions | null; parent_template_id: number | null }) =>
      updateProposalTemplate(templateIdNum, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["proposal-template", templateIdNum],
      });
      queryClient.invalidateQueries({
        queryKey: ["proposal-templates", activeVersionId],
      });
      toast.success("Parametres enregistres");
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });

  const handleStartEditName = () => {
    setEditName(template?.name || "");
    setIsEditingName(true);
  };

  const handleSaveName = () => {
    if (editName.trim() && editName !== template?.name) {
      updateNameMutation.mutate(editName.trim());
    } else {
      setIsEditingName(false);
    }
  };

  const handleSave = (pages: TemplatePage[], inheritedPageAdditions?: Record<string, CanvasData> | null) => {
    updateMutation.mutate({ pages, inheritedPageAdditions });
  };

  const handleSaveSettings = () => {
    updateSettingsMutation.mutate({
      priority,
      selection_conditions: selectionConditions,
      parent_template_id: parentTemplateId,
    });
  };

  const handleAddCondition = () => {
    const newConditions: VisibilityConditions = selectionConditions
      ? {
          ...selectionConditions,
          conditions: [
            ...selectionConditions.conditions,
            { field_slug: "", operator: "equals", value: "" },
          ],
        }
      : { logic: "AND", conditions: [{ field_slug: "", operator: "equals", value: "" }] };
    setSelectionConditions(newConditions);
  };

  const handleRemoveCondition = (index: number) => {
    if (!selectionConditions) return;
    const newConditions = selectionConditions.conditions.filter((_, i) => i !== index);
    if (newConditions.length === 0) {
      setSelectionConditions(null);
    } else {
      setSelectionConditions({ ...selectionConditions, conditions: newConditions });
    }
  };

  const handleUpdateCondition = (index: number, updates: Partial<{ field_slug: string; operator: ConditionOperator; value: string }>) => {
    if (!selectionConditions) return;
    const newConditions = [...selectionConditions.conditions];
    newConditions[index] = { ...newConditions[index], ...updates };
    setSelectionConditions({ ...selectionConditions, conditions: newConditions });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Chargement..." subtitle={simulator?.name}>
          <Button variant="ghost" disabled>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
        </Header>
        <div className="flex-1 flex items-center justify-center">
          <Skeleton className="w-[800px] h-[600px]" />
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Erreur" subtitle={simulator?.name}>
          <Button variant="ghost" asChild>
            <Link
              to="/admin/simulators/$id"
              params={{ id }}
              search={{ tab: "templates", version: activeVersionId || undefined }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Link>
          </Button>
        </Header>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">
            Template non trouve ou erreur de chargement
          </p>
        </div>
      </div>
    );
  }

  return (
    <AdminGuard>
    <div className="flex flex-col h-full">
      <Header
        title={
          isEditingName ? (
            <div className="flex items-center gap-2">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="h-8 w-64"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveName();
                  if (e.key === "Escape") setIsEditingName(false);
                }}
                autoFocus
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleSaveName}
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setIsEditingName(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {template.name}
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={handleStartEditName}
              >
                <Pencil className="h-3 w-3" />
              </Button>
            </div>
          )
        }
        subtitle={simulator?.name}
      >
        <Button variant="ghost" asChild>
          <Link
            to="/admin/simulators/$id"
            params={{ id }}
            search={{ tab: "templates", version: activeVersionId || undefined }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Link>
        </Button>
      </Header>

      {/* Selection settings collapsible */}
      <div className="px-4 border-b bg-muted/30">
        <Collapsible open={settingsOpen} onOpenChange={setSettingsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between py-2 h-auto">
              <div className="flex items-center gap-2">
                <Settings2 className="h-4 w-4" />
                <span className="text-sm font-medium">Parametres de selection automatique</span>
              </div>
              <div className="flex items-center gap-2">
                {selectionConditions && selectionConditions.conditions.length > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    <Filter className="h-3 w-3" />
                    {selectionConditions.conditions.length} condition{selectionConditions.conditions.length > 1 ? "s" : ""}
                  </Badge>
                )}
              </div>
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="py-4 space-y-4">
              {/* Parent template info and selector */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <Link2 className="h-4 w-4" />
                  Template parent
                </Label>
                
                {/* Show current parent info if has a parent */}
                {template.parent_template && (
                  <div className="flex items-center gap-2 p-2 rounded bg-blue-50 border border-blue-200">
                    <Badge variant="outline" className="text-blue-600 border-blue-300">
                      Herite de : {template.parent_template.name}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      ({template.parent_template.pages_data?.length || 0} pages heritees)
                    </span>
                  </div>
                )}
                
                {/* Parent selector - only show if this template has no children */}
                {(() => {
                  // Find if this template has children (is a parent itself)
                  const templateInList = potentialParents?.items.find((t) => t.id === templateIdNum);
                  const hasChildren = templateInList?.children_count && templateInList.children_count > 0;
                  
                  if (hasChildren) {
                    return (
                      <div className="flex items-center gap-2 p-2 rounded bg-purple-50 border border-purple-200">
                        <Badge variant="outline" className="text-purple-600 border-purple-300 gap-1">
                          <Users className="h-3 w-3" />
                          {templateInList?.children_count} template(s) enfant(s)
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          Ce template ne peut pas avoir de parent car il a des enfants.
                        </span>
                      </div>
                    );
                  }
                  
                  return (
                    <Select
                      value={parentTemplateId?.toString() ?? "none"}
                      onValueChange={(value) => setParentTemplateId(value === "none" ? null : Number(value))}
                    >
                      <SelectTrigger className="w-full max-w-md">
                        <SelectValue placeholder="Aucun (template independant)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun (template independant)</SelectItem>
                        {availableParents.map((t) => (
                          <SelectItem key={t.id} value={t.id.toString()}>
                            {t.name}
                            {t.pages_data?.length > 0 && ` (${t.pages_data.length} pages)`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                })()}
                
                <p className="text-sm text-muted-foreground">
                  Si vous choisissez un template parent, ce template heritera automatiquement de toutes ses pages.
                </p>
              </div>

              {/* Priority input */}
              <div className="flex items-center gap-4">
                <Label className="text-sm font-medium w-16">Priorite</Label>
                <Input
                  type="number"
                  value={priority}
                  onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
                  className="w-24"
                  min={0}
                />
                <span className="text-sm text-muted-foreground">
                  Les templates avec une priorite plus elevee sont evalues en premier.
                </span>
              </div>

              {/* Selection conditions */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Conditions de selection</Label>
                  <Button variant="outline" size="sm" onClick={handleAddCondition}>
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter
                  </Button>
                </div>

                {selectionConditions && selectionConditions.conditions.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-muted-foreground">Selectionner ce template si</span>
                      <Select
                        value={selectionConditions.logic}
                        onValueChange={(value: ConditionLogic) =>
                          setSelectionConditions({
                            ...selectionConditions,
                            logic: value,
                          })
                        }
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AND">Toutes</SelectItem>
                          <SelectItem value="OR">Au moins une</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-sm text-muted-foreground">des conditions sont remplies :</span>
                    </div>

                    <div className="space-y-2">
                      {selectionConditions.conditions.map((condition, index) => (
                        <div key={index} className="flex items-center gap-2 flex-wrap">
                          {/* Field selector with search */}
                          <FieldSearchSelector
                            value={condition.field_slug || ""}
                            onSelect={(slug) => handleUpdateCondition(index, { field_slug: slug })}
                            fields={allFieldsForConditions}
                            placeholder="Rechercher un champ..."
                          />

                          {/* Operator selector */}
                          <Select
                            value={condition.operator}
                            onValueChange={(value: ConditionOperator) =>
                              handleUpdateCondition(index, { operator: value })
                            }
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(operatorLabels).map(([op, label]) => (
                                <SelectItem key={op} value={op}>
                                  {label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Value input */}
                          {!["is_empty", "is_not_empty"].includes(condition.operator) && (
                            <Input
                              value={String(condition.value ?? "")}
                              onChange={(e) => handleUpdateCondition(index, { value: e.target.value })}
                              placeholder="Valeur..."
                              className="w-32"
                            />
                          )}

                          {/* Remove button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleRemoveCondition(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {(!selectionConditions || selectionConditions.conditions.length === 0) && (
                  <p className="text-sm text-muted-foreground italic">
                    Aucune condition. Ce template sera utilise par defaut si aucun autre template conditionnel ne correspond.
                  </p>
                )}
              </div>

              {/* Save button */}
              <div className="flex justify-end pt-2">
                <Button onClick={handleSaveSettings} disabled={updateSettingsMutation.isPending}>
                  Enregistrer les parametres
                </Button>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>

      <div className="flex-1 overflow-hidden">
        <TemplateEditor
          initialPages={template.resolved_pages_data}
          ownPagesStartIndex={template.parent_template ? template.parent_template.pages_data?.length || 0 : 0}
          onSave={handleSave}
          isSaving={updateMutation.isPending}
          outputFields={allOutputFields}
          formFields={allFormFields}
        />
      </div>
    </div>
    </AdminGuard>
  );
}
